import { createSlice } from "@reduxjs/toolkit";
import { ListViewOption } from "core/components/listToggle/listToggleControl";
import { WipFilterModel } from "core/models/WipFilterModel";
import { logger } from "utils/logger";
import * as styleService from "services/styleService";
import { MediaFilterModel } from "core/models";
import ResultSet from "core/models/ResultSet";
import moment from "moment";
import { EMPTY_DASHBOARD_FILTER } from "./inventoryAggregates";
import {
  deriveInventories,
  SAMPLE_STATUSES,
  URGENCY_LEVELS,
} from "./inventoryStatus";

// Placeholder data for the Inventory dashboard, standing in until the real
// inventory reporting endpoints are wired up.

// Small seeded PRNG so the mock samples are stable between renders/reloads.
const seededRandom = (seed) => () => {
  seed = (seed * 16807) % 2147483647;
  return (seed - 1) / 2147483646;
};

const buildUrgencySamples = (count) => {
  const rand = seededRandom(35068);
  return Array.from({ length: count }, (_, index) => ({
    id: `S-${10400 + index}`,
    status: SAMPLE_STATUSES[Math.floor(rand() * SAMPLE_STATUSES.length)],
    urgency: URGENCY_LEVELS[Math.floor(rand() * URGENCY_LEVELS.length)].name,
    daysSinceScanned: Math.round(2 + rand() * 56),
  }));
};

const buildInventoryOverview = (days) => {
  const rand = seededRandom(786);
  const start = new Date(2026, 9, 1);
  let inventory = 2600;
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    inventory += Math.round((rand() - 0.4) * 140);
    return {
      date: date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      Inventory: inventory,
      "Checked In": Math.round(120 + rand() * 180),
      Retouching: Math.round(60 + rand() * 120),
      Distributed: Math.round(90 + rand() * 160),
      "Errant Scans": Math.round(2 + rand() * 12),
    };
  });
};

const mockDashboard = {
  stats: {
    totalInventory: 3000,
    errantScans: 120,
    checkedIn: 842,
    inRetouching: 316,
    distributed: 1200,
  },
  // % change vs the previous week, per stat.
  statChanges: {
    totalInventory: 5,
    errantScans: -12,
    checkedIn: 8,
    inRetouching: -3,
    distributed: 14,
  },
  inventoryOverview: buildInventoryOverview(21),
  urgencySamples: buildUrgencySamples(72),
  // Stages sum to stats.totalInventory (3000) - every unit of inventory is
  // accounted for in exactly one stage.
  itemsPerStage: [
    { label: "Not Yet Actioned", value: 700 },
    { label: "Acquired", value: 150 },
    { label: "Scheduled", value: 300 },
    { label: "Captured", value: 20 },
    { label: "Post Production", value: 480 },
    { label: "Distributed", value: 1200 },
    { label: "Returned", value: 150 },
  ],
  categories: [
    { label: "Women's Apparel", value: 1040 },
    { label: "Men's Apparel", value: 820 },
    { label: "Home", value: 510 },
    { label: "Beauty", value: 380 },
    { label: "Kids", value: 250 },
  ],
  // Volumes sum to stats.errantScans (120) - every errant scan has exactly
  // one reason.
  errantReasons: [
    { label: "No Barcode Match", value: 34 },
    { label: "Duplicate Scan", value: 28 },
    { label: "Style Discontinued", value: 18 },
    { label: "Wrong Search Code", value: 17 },
    { label: "Missing in Feed", value: 14 },
    { label: "No Barcode", value: 9 },
  ],
  // Values sum to stats.totalInventory (3000). Colours come from
  // inventoryColors.js.
  channels: [
    { label: "e-comm", value: 1500 },
    { label: "Campaign", value: 900 },
    { label: "Packaging", value: 600 },
  ],
};

const inventorySlice = createSlice({
  name: "inventorySlice",
  initialState: {
    // wips: [],
    // selectedWip: null,
    selectedStyles: [],
    displayPrefs: { mediaDisplayType: ListViewOption.GRID },
    inventoryFilter: { ...new WipFilterModel() },
    styleFilter: { ...new WipFilterModel() },
    styleMediaFilter: { ...new MediaFilterModel() },
    // Filters every dashboard panel (see buildInventoryDashboard).
    dashboardFilter: { ...EMPTY_DASHBOARD_FILTER },
    // Dashboard view choices, kept across refreshes. null means the default.
    //   visibleSeries    Inventory Overview series shown
    //   urgencyStatuses  Sample Urgency status chips selected
    //   urgencyView      Sample Urgency "scatter" | "matrix"
    dashboardPrefs: { visibleSeries: null, urgencyStatuses: null, urgencyView: null },
    // The list's column choices (column id -> shown); null means the
    // column defaults (meta.show).
    listColumns: null,
    styles: [],
    selectedStyle: null,
    dashboard: mockDashboard,
    inventories: { data: [], complete: false },
    inventoriesCursor: null,
    inventoriesLoading: false,
    inventoriesError: null,
    scans: { data: [], complete: false },
    scansCursor: null,
    scansLoading: false,
    scansError: null,
    // standard props
    error: null,
    success: null,
    loading: false,
    action: null,
  },
  reducers: {
    setDisplayPrefs(state, action) {
      state.displayPrefs = { ...action.payload };
    },
    selectStyle(state, action) {
      logger.log("Setting Style: ", action.payload);
      state.selectedStyle = action.payload ? { ...action.payload } : null;
    },
    selectStyles(state, action) {
      logger.log("setting selected wips", action.payload);
      state.selectedStyles = action.payload ? [...action.payload] : [];
    },
    setInventoryFilter(state, action) {
      logger.log("Setting Inventory Filter: ", action.payload);
      state.inventoryFilter = action.payload ? { ...action.payload } : null;
    },
    setStyleFilter(state, action) {
      logger.log("Setting Style Filter: ", action.payload);
      state.styleFilter = action.payload ? { ...action.payload } : null;
    },
    setStyleMediaFilter(state, action) {
      logger.log("Setting Style Filter: ", action.payload);
      state.styleMediaFilter = action.payload ? { ...action.payload } : null;
    },
    setListColumns(state, action) {
      state.listColumns = action.payload ? { ...action.payload } : null;
    },
    setDashboardPrefs(state, action) {
      state.dashboardPrefs = { ...state.dashboardPrefs, ...action.payload };
    },
    setDashboardFilter(state, action) {
      state.dashboardFilter = { ...EMPTY_DASHBOARD_FILTER, ...action.payload };
    },
    setError(state, action) {
      state.error = action.payload;
    },

    //////////////////////
    //
    // GET STYLES
    //
    //////////////////////
    getStylesStart(state, action) {
      state.success = null;
      state.error = null;
    },
    getStylesSuccess(state, action) {
      logger.log("getStyleSuccess : SUCCESS");

      state.loading = false;
      state.success = { type: action.type, message: "Get Styles Success" };

      state.styles = [...action.payload];
      //state.openIssuesCount = action.payload.open_issues_count
      state.error = null;
    },
    getStylesFailure(state, action) {
      logger.log(state, action, "fail");
      state.error = action.payload;
    },

    //////////////////////
    //
    // SINGLE STYLE
    //
    //////////////////////
    getStyleStart(state, action) {
      state.success = null;
      state.error = null;
    },
    getStyleSuccess(state, action) {
      logger.log("getWipSuccess : SUCCESS");

      state.loading = false;
      state.success = {
        type: action.type,
        message: "Get Wip Success",
        data: action.payload,
      };
      // if(action.payload.urls){
      //   state.selectedProject = {...state.selectedProject, urls:action.payload.urls}
      // }
      state.selectedStyle = action.payload ? { ...action.payload } : null;
      //state.openIssuesCount = action.payload.open_issues_count
      state.error = null;
    },

    getLatestStylesSuccess(state, action) {
      logger.log("getWipSuccess : SUCCESS");

      state.loading = false;
      state.success = {
        type: action.type,
        message: "Get Wip Success",
        data: action.payload,
      };
      // if(action.payload.urls){
      //   state.selectedProject = {...state.selectedProject, urls:action.payload.urls}
      // }
      const updateMap = new Map(
        action.payload.map((item) => [item.styleNumber, item]),
      );

      const styles = [...state.styles].map((item) => {
        const update = updateMap.get(item.styleNumber);
        return update ? { ...item, ...update } : item;
      });

      state.styles = styles;
      //state.openIssuesCount = action.payload.open_issues_count
      state.error = null;
    },

    getStyleFailure(state, action) {
      logger.log(state, action, "fail");
      state.error = action.payload;
    },

    getWipTimelineSuccess(state, action) {
      logger.log("getWipSuccess : SUCCESS");

      state.loading = false;
      state.success = {
        type: action.type,
        message: "Get Wip Timeline Success",
      };

      if (state.selectedWip) {
        state.selectedWip = {
          ...state.selectedWip,
          timeline: action.payload.Timeline,
        };
      }
      //state.openIssuesCount = action.payload.open_issues_count
      state.error = null;
    },

    //////////////////////
    //
    // Inventories
    //
    //////////////////////
    // Inventories keep their own loading/error so paging doesn't reset the
    // shared success/error the styles fetch is watched by.
    getInventoriesStart(state, action) {
      state.inventoriesLoading = true;
      state.inventoriesError = null;
    },
    // Payload is a ResultSet (same shape as wipSlice getScheduleSuccess):
    // data.items for the pages fetched so far, data.cursor for the next page
    // and data.complete once the last page is in.
    getInventoriesSuccess(state, action) {
      logger.log("getInventoriesSuccess : SUCCESS");

      const { items = [], cursor, concatItems, complete } = action.payload.data;

      const existingItems =
        concatItems && Array.isArray(state.inventories.data)
          ? [...state.inventories.data]
          : [];
      const mergedItems = concatItems ? existingItems.concat(items) : [...items];

      state.inventoriesLoading = !complete;
      state.inventoriesCursor = cursor ?? null;
      state.inventories = {
        ...action.payload,
        data: mergedItems,
        complete: complete ?? false,
      };
    },

    getInventoriesFailure(state, action) {
      logger.log(state, action, "fail");
      state.inventoriesError = action.payload;
      state.inventoriesLoading = false;
    },

    //////////////////////
    //
    // Scans
    //
    //////////////////////
    // Same paging/ResultSet handling as inventories, with its own state.
    getScansStart(state, action) {
      state.scansLoading = true;
      state.scansError = null;
    },
    getScansSuccess(state, action) {
      logger.log("getScansSuccess : SUCCESS");

      const { items = [], cursor, concatItems, complete } = action.payload.data;

      const existingItems =
        concatItems && Array.isArray(state.scans.data)
          ? [...state.scans.data]
          : [];
      const mergedItems = concatItems ? existingItems.concat(items) : [...items];

      state.scansLoading = !complete;
      state.scansCursor = cursor ?? null;
      state.scans = {
        ...action.payload,
        data: mergedItems,
        complete: complete ?? false,
      };
    },

    getScansFailure(state, action) {
      logger.log(state, action, "fail");
      state.scansError = action.payload;
      state.scansLoading = false;
    },


    
    clearNotifications(state, action) {
      state.error = null;
      state.success = null;
    },

    onServiceStart(state, action) {
      state.success = null;
      state.loading = true;
      state.error = null;
      // state.action = action.type;
    },
    onServiceFailure(state, action) {
      logger.log(state, action, "fail");
      state.error = action.payload;
      state.loading = false;
    },
  },
});

// STYLES
export const fetchStylesByUnit = (criteria) => async (dispatch) => {
  try {
    dispatch(getStylesStart());
    const data = await styleService.getStylesByUnit(criteria);
    // The service returns SampleModel instances; the store needs plain objects.
    dispatch(getStylesSuccess((data ?? []).map((item) => ({ ...item }))));
  } catch (err) {
    logger.log("err", err);
    dispatch(getStylesFailure(err));
  }
};

export const fetchStyle = (id) => async (dispatch) => {
  try {
    dispatch(getStyleStart());
    // const data = await getStyleTimeline({ id: criteria.keyword });
    const data = await styleService.getStyle(id);
    dispatch(getStyleSuccess(data ? { ...data } : data));
  } catch (err) {
    logger.log("err", err);
    dispatch(getStyleFailure(err));
  }
};

export const fetchLatestStyles = (items) => async (dispatch) => {
  try {
    dispatch(getStyleStart());
    // const data = await getStyleTimeline({ id: criteria.keyword });
    // const data = await styleService.getStyle(id);

    const results = await Promise.all(
      items.map((id) => styleService.getStyle(id)),
    );

    dispatch(getLatestStylesSuccess(results.map((item) => ({ ...item }))));
  } catch (err) {
    logger.log("err", err);
    dispatch(getStylesFailure(err));
  }
};

// Redux actions must be serialisable, so send a ResultSet as a plain object
// with an ISO timestamp (ResultSet's default) rather than the class + a Date.
const resultSetPayload = (data, id, type) => ({
  ...new ResultSet(data, id, null, type),
});

export const fetchInventories =
  (criteria, cursor, fetchAll = true) =>
  async (dispatch) => {
    try {
      dispatch(getInventoriesStart());

      let tmpCursor = cursor ?? "";
      // The first page replaces what's loaded unless resuming from a cursor;
      // later pages always append.
      let concatItems = Boolean(cursor);
      // One "today" for the whole fetch so every page is judged the same.
      const today = moment();

      do {
        const data = await styleService.getInventories(criteria, tmpCursor);
        tmpCursor = data.nextCursor;

        // Publish each page as it lands so the UI can render progressively.
        // Each record gets derivedStatus and priority (see inventoryStatus.js).
        dispatch(
          getInventoriesSuccess(
            resultSetPayload(
              {
                items: deriveInventories(data.items, today),
                complete: !tmpCursor,
                cursor: tmpCursor,
                concatItems,
              },
              JSON.stringify(criteria),
              "inventories",
            ),
          ),
        );
        concatItems = true;
      } while (fetchAll && tmpCursor);
    } catch (err) {
      logger.log("err", err);
      dispatch(getInventoriesFailure(err));
    }
  };

// Whether a fetched ResultSet can be reused: every page is in, it's the
// expected type and it's younger than maxAge (ms).
const isFresh = (resultSet, type, maxAge) =>
  Boolean(resultSet?.complete) &&
  resultSet.type === type &&
  Date.now() - new Date(resultSet.timestamp).getTime() < maxAge;

// Load inventory only when needed: skipped while a fetch is already running
// or the loaded data is still fresh, so moving between the dashboard and the
// list doesn't replace what's on screen with a full reload.
export const loadInventories = (maxAge) => (dispatch, getState) => {
  const { inventories, inventoriesLoading } = getState().inventoryReducer;
  if (inventoriesLoading || isFresh(inventories, "inventories", maxAge)) return;
  dispatch(fetchInventories({}));
};

export const fetchScans =
  (criteria, cursor, fetchAll = true) =>
  async (dispatch) => {
    try {
      dispatch(getScansStart());

      let tmpCursor = cursor ?? "";
      // The first page replaces what's loaded unless resuming from a cursor;
      // later pages always append.
      let concatItems = Boolean(cursor);

      do {
        const data = await styleService.getInventoryScans(criteria, tmpCursor);
        tmpCursor = data.nextCursor;

        // Publish each page as it lands so the UI can render progressively.
        dispatch(
          getScansSuccess(
            resultSetPayload(
              {
                items: data.items,
                complete: !tmpCursor,
                cursor: tmpCursor,
                concatItems,
              },
              JSON.stringify(criteria),
              "scans",
            ),
          ),
        );
        concatItems = true;
      } while (fetchAll && tmpCursor);
    } catch (err) {
      logger.log("err", err);
      dispatch(getScansFailure(err));
    }
  };

// Same as loadInventories, for errant scans.
export const loadScans = (maxAge) => (dispatch, getState) => {
  const { scans, scansLoading } = getState().inventoryReducer;
  if (scansLoading || isFresh(scans, "scans", maxAge)) return;
  dispatch(fetchScans({}));
};

export const {
  // selectWip,
  selectStyle,
  selectStyles,
  setError,
  setInventoryFilter,
  setDashboardFilter,
  setDashboardPrefs,
  setListColumns,

  setDisplayPrefs,

  styleMediaFilter,
  setStyleMediaFilter,

  getWipTimelineSuccess,

  getStylesStart,
  getStylesSuccess,
  getStylesFailure,

  getStyleStart,
  getStyleSuccess,
  getStyleFailure,

  getInventoriesStart,
  getInventoriesSuccess,
  getInventoriesFailure,

  getScansStart,
  getScansSuccess,
  getScansFailure,

  onServiceStart,
  onServiceFailure,

  getLatestStylesSuccess,

  clearNotifications,
} = inventorySlice.actions;

export default inventorySlice.reducer;
